import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { TrendingUp, TrendingDown, Activity, Target, DollarSign, Users, Building, Briefcase, Loader2, ArrowRight, Download, FileText } from "lucide-react";
import axios from "axios";
import { downloadCSV, downloadJSON, downloadData } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardSection = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    
    // Set up real-time updates every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`);
      setDashboardData(response.data);
      setLastUpdated(new Date(response.data.last_updated));
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ metric, icon: Icon, color, metricId }) => {
    const navigate = useNavigate();
    
    const handleCardClick = () => {
      // metricId now contains the full dashboard path
      navigate(metricId);
    };

    return (
      <Card 
        className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
        onClick={handleCardClick}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-gray-600">
            {metric.title}
          </CardTitle>
          <Icon className={`w-4 h-4 ${color}`} />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-gray-900 mb-2">
            {metric.value}{metric.unit}
          </div>
          <div className="flex items-center justify-between">
            <Progress 
              value={typeof metric.value === 'number' && metric.unit === '%' ? metric.value : 75} 
              className="flex-1 mr-3 h-2"
            />
            <Badge 
              variant={metric.trend.startsWith('+') ? 'default' : 'secondary'}
              className={`text-xs ${
                metric.trend.startsWith('+') 
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              {metric.trend.startsWith('+') ? (
                <TrendingUp className="w-3 h-3 mr-1" />
              ) : (
                <TrendingDown className="w-3 h-3 mr-1" />
              )}
              {metric.trend}
            </Badge>
          </div>
        </CardContent>
        <div className={`absolute top-0 right-0 w-1 h-full ${
          color.includes('orange') ? 'bg-orange-500' : 
          color.includes('blue') ? 'bg-blue-500' :
          color.includes('green') ? 'bg-green-500' : 'bg-purple-500'
        }`} />
        
        {/* Click indicator */}
        <div className="absolute top-4 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white/90 backdrop-blur-sm rounded-full p-2">
            <ArrowRight className="w-4 h-4 text-orange-600" />
          </div>
        </div>
      </Card>
    );
  };

  if (loading && !dashboardData) {
    return (
      <section id="dashboard" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-pink-500" />
            <p className="text-gray-600">Loading real-time dashboard data...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error && !dashboardData) {
    return (
      <section id="dashboard" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="dashboard" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-6">
            <Activity className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            WCD Performance Dashboard
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real-time insights and analytics driving women empowerment and child development across Gujarat
          </p>
          {lastUpdated && (
            <p className="text-sm text-gray-500 mt-2">
              Last updated: {lastUpdated.toLocaleString()}
              {loading && <span className="ml-2 inline-flex items-center"><Loader2 className="w-3 h-3 animate-spin mr-1" />Updating...</span>}
            </p>
          )}
        </div>

        {dashboardData && (
          <>
            {/* Child Development Metrics */}
            <div className="mb-12">
              <div className="flex items-center mb-6">
                <Target className="w-6 h-6 text-pink-500 mr-3" />
                <h3 className="text-2xl font-semibold text-gray-900">Child Development Metrics</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardData.childDevelopment.map((metric, index) => {
                  const icons = [Activity, Target, Users, Building];
                  const colors = ['text-pink-500', 'text-blue-500', 'text-green-500', 'text-purple-500'];
                  // Map to actual WCD program dashboards
                  const dashboardLinks = [
                    '/dashboards/growth-monitoring',  // Growth Monitoring Coverage
                    '/dashboards/hcm',                 // Hot Cooked Meal Coverage
                    '/dashboards/hcm',                 // Fortified Rice Coverage (same dashboard)
                    '/dashboards/infrastructure'       // AWC Infrastructure Score
                  ];
                  return (
                    <MetricCard 
                      key={index}
                      metric={metric}
                      icon={icons[index]}
                      color={colors[index]}
                      metricId={dashboardLinks[index]}
                    />
                  );
                })}
              </div>
            </div>

            {/* Women Empowerment Indicators */}
            <div>
              <div className="flex items-center mb-6">
                <Users className="w-6 h-6 text-pink-500 mr-3" />
                <h3 className="text-2xl font-semibold text-gray-900">Women Empowerment Indicators</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardData.womenEmpowerment.map((metric, index) => {
                  const icons = [TrendingUp, Users, Briefcase, Activity];
                  const colors = ['text-green-500', 'text-blue-500', 'text-purple-500', 'text-pink-500'];
                  // Map to actual WCD program dashboards
                  const dashboardLinks = [
                    '/dashboards/purna',               // Adolescent Girls Enrolled (PURNA)
                    '/dashboards/enrollment-dropout',  // Beneficiary Retention Rate
                    '/dashboards/frs',                 // Digital Authentication Rate
                    '/dashboards/thr'                  // THR Distribution Efficiency
                  ];
                  return (
                    <MetricCard 
                      key={index}
                      metric={metric}
                      icon={icons[index]}
                      color={colors[index]}
                      metricId={dashboardLinks[index]}
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Quick Actions */}
        <div className="mt-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Real-Time Data Access</h3>
            <p className="text-gray-300">Access detailed analytics and generate custom reports</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <button 
              onClick={fetchDashboardData}
              className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/10 disabled:opacity-50"
              disabled={loading}
            >
              <Activity className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </span>
            </button>
            <button 
              onClick={() => navigate('/generate-report')}
              className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/10"
            >
              <TrendingUp className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Generate Report</span>
            </button>
            <button 
              onClick={() => navigate('/set-kpis')}
              className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/10"
            >
              <Target className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Set KPIs</span>
            </button>
            <button 
              onClick={() => downloadCSV(downloadData.schemes, 'wcd_schemes_data')}
              className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/10"
            >
              <Download className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Download CSV</span>
            </button>
            <button 
              onClick={() => downloadJSON(downloadData, 'wcd_complete_data')}
              className="bg-white/10 hover:bg-white/20 text-white p-4 rounded-xl transition-all duration-300 backdrop-blur-sm border border-white/10"
            >
              <FileText className="w-6 h-6 mx-auto mb-2" />
              <span className="font-medium">Download JSON</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardSection;